<template>
    <div class="event-card" @click="$emit('open', event)">
        <div class="event-card__top" @click.stop>
            <EventStatusPill v-if="event.status !== 'live'" :status="event.status"></EventStatusPill>
            <span v-else></span>
            <BtnPop ref="pop" :left="-180">
                <div slot="target" class="event-card__more"><GI ws="24" hs="24" name="brand_more"></GI></div>
                <div slot="main" class="event-card__menu" @click="$refs.pop.closePopup()">
                    <div v-for="item in menu" :key="item.action" :class="['event-card__menu-item', item.danger && 'event-card__menu-item--danger']" @click="$emit('action', item.action, event)">
                        <GI ws="20" hs="20" :name="item.icon"></GI>
                        <span>{{ langJson(item.label) }}</span>
                    </div>
                </div>
            </BtnPop>
        </div>
        <div class="event-card__image" :style="event.ticketImageUrl ? { backgroundImage: `url(${event.ticketImageUrl})` } : {}"></div>
        <h3 class="event-card__title">{{ event.title }}</h3>
        <p class="event-card__date">{{ dates }}</p>
        <p class="event-card__desc">{{ event.description || langJson('No description yet') }}</p>
        <div class="event-card__footer">
            <div class="event-card__chips">
                <span class="event-card__chip event-card__chip--sold">{{ event.soldCount.toLocaleString() }} {{ langJson('Sold') }}</span>
                <span v-if="countdown !== null && event.status !== 'ended'" class="event-card__chip event-card__chip--days">
                    {{ countdown === 0 ? langJson('Today') : `${countdown} ${langJson('days to go')}` }}
                </span>
            </div>
            <span class="event-card__chevron">›</span>
        </div>
        <p v-if="event.status === 'rejected' && event.rejectionReason" class="event-card__reason">{{ event.rejectionReason }}</p>
    </div>
</template>
<script>
import { daysToGo, formatEventDates } from '@/assets/js/organizerEvent'

export default {
    props: {
        event: { type: Object, required: true },
    },
    computed: {
        dates() {
            return formatEventDates(this.event.eventDate, this.event.eventEndDate, this.event.timezone)
        },
        countdown() {
            return daysToGo(this.event.eventDate)
        },
        menu() {
            const { status } = this.event
            const items = [{ action: 'preview', icon: 'brand_eye', label: status === 'live' ? 'View as participant' : 'Preview' }]
            if (['draft', 'rejected', 'approved', 'live'].includes(status)) {
                items.push({ action: 'edit', icon: 'brand_edit', label: 'Edit event' })
            }
            items.push({ action: 'overview', icon: 'brand_clock', label: 'Event overview' })
            if (status === 'live') {
                items.push({ action: 'end', icon: 'brand_trash', label: 'End event', danger: true })
            }
            if (['draft', 'rejected'].includes(status)) {
                items.push({ action: 'delete', icon: 'brand_trash', label: 'Delete', danger: true })
            }
            return items
        },
    },
}
</script>
<style lang="scss" scoped>
.event-card {
    background: var(--color_white);
    border-radius: 24px;
    padding: 16px 24px 24px;
    box-sizing: border-box;
    box-shadow: 4px 8px 24px rgba(187, 196, 221, 0.08);
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;

    &:hover {
        box-shadow: 4px 8px 24px rgba(187, 196, 221, 0.4);
    }

    &__top {
        display: flex;
        justify-content: space-between;
        align-items: center;
        min-height: 28px;
    }
    &__more {
        cursor: pointer;
        display: flex;
    }
    &__menu {
        padding: 24px;
        background: var(--color_white);
        border: 1px solid var(--border_color);
        border-radius: 24px;
        box-shadow: 4px 8px 24px rgba(187, 196, 221, 0.08);
        display: flex;
        flex-direction: column;
        gap: 16px;
    }
    &__menu-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-family: 'Lato';
        font-size: 14px;
        white-space: nowrap;
        color: var(--font_deep_color);
        cursor: pointer;

        &--danger {
            color: var(--danger_color);
        }
    }
    &__image {
        width: 100%;
        aspect-ratio: 16 / 9;
        border-radius: 16px;
        background: #e4e7ec center / cover no-repeat;
        margin-bottom: 8px;
    }
    &__title {
        font-family: 'Inter';
        font-weight: 700;
        font-size: 18px;
        line-height: 26px;
        color: var(--font_deep_color);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    &__date {
        font-family: 'Lato';
        font-weight: 700;
        font-size: 14px;
        color: var(--main_color);
    }
    &__desc {
        font-family: 'Lato';
        font-size: 13px;
        line-height: 18px;
        color: var(--font_color);
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        min-height: 36px;
    }
    &__footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 8px;
    }
    &__chips {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }
    &__chip {
        padding: 6px 12px;
        border-radius: 50px;
        font-family: 'Lato';
        font-size: 12px;

        &--sold {
            background: #f1e9ff;
            color: #8b5cf6;
        }
        &--days {
            background: #e7f8ee;
            color: #12a150;
        }
    }
    &__chevron {
        font-size: 24px;
        color: var(--font_modif_color);
    }
    &__reason {
        font-family: 'Lato';
        font-size: 12px;
        color: var(--danger_color);
    }
}

@media only screen and (max-width: 1080px) {
    .event-card {
        padding: 12px;
        border-radius: 16px;
    }
}
</style>
